/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { noop, uniqueId } from 'lodash';

import type { InlineNotificationProps, PageNotificationProps } from '@coursera/cds-core';
import { Grid, InlineNotification, PageNotification } from '@coursera/cds-core';

// https://github.com/webedx-spark/cds/issues/1179
const cdsWorkaroundForCenteringContentWithoutATitle = css`
  .cds-localNotification-content {
    align-items: center !important;
  }

  svg {
    display: flex;
  }

  align-items: center !important;
`;

type Commons = {
  id?: string;
  title?: string;
  addedAt?: number;
  namespace: string;
  dismissable?: boolean;
};

export type EnterpriseNotification =
  | (Commons & {
      severity?: InlineNotificationProps['severity'];
      children: InlineNotificationProps['children'];
      inlineAction?: InlineNotificationProps['inlineAction'];
      component?: 'InlineNotification';
    })
  | (Commons & {
      severity?: PageNotificationProps['severity'];
      children: PageNotificationProps['children'];
      inlineAction?: PageNotificationProps['inlineAction'];
      component: 'PageNotification';
    });

const NotificationsContext = React.createContext<{
  renderNotifications: (namespace: string, options?: { noMargin?: boolean }) => JSX.Element | null;
  addNotification: (notification: EnterpriseNotification) => void;
  clearNotifications: (namespace: string) => void;
}>({ renderNotifications: () => null, addNotification: noop, clearNotifications: noop });

export const Notifications: React.VFC<{
  noMargin?: boolean;
  notifications: EnterpriseNotification[];
  onDismiss?: (id: EnterpriseNotification['id']) => void;
}> = ({ noMargin = false, notifications, onDismiss }) => {
  if (!notifications.length) return null;
  return (
    <Grid
      container
      spacing={12}
      css={css`
        margin-bottom: ${noMargin ? 0 : 'var(--cds-spacing-300)'};
      `}
    >
      {notifications.map((notification) => (
        <Grid item xs={12} key={notification.id}>
          {notification.component === 'PageNotification' ? (
            <PageNotification
              role="alert"
              severity={notification.severity}
              inlineAction={notification.inlineAction}
              onDismiss={notification.dismissable && onDismiss ? () => onDismiss(notification.id) : undefined}
              title={notification.title}
            >
              {notification.children}
            </PageNotification>
          ) : (
            <InlineNotification
              role="alert"
              severity={notification.severity}
              inlineAction={notification.inlineAction}
              onDismiss={notification.dismissable && onDismiss ? () => onDismiss(notification.id) : undefined}
              title={notification.title}
              css={notification.title ? undefined : cdsWorkaroundForCenteringContentWithoutATitle}
            >
              {notification.children}
            </InlineNotification>
          )}
        </Grid>
      ))}
    </Grid>
  );
};
export const NotificationsProvider: React.FC<{}> = ({ children }) => {
  const [allNotifications, setAllNotifications] = React.useState<EnterpriseNotification[]>([]);

  const renderNotifications = React.useCallback(
    (namespace: string, options?: { noMargin?: boolean }) => {
      const staleCutOff = Date.now() - 10 * 1000; // 10 seconds ago
      const pageNotifications = allNotifications.filter(
        (_) => _.namespace === namespace && _.addedAt && _.addedAt > staleCutOff
      );
      return (
        <Notifications
          notifications={pageNotifications}
          onDismiss={(id) => setAllNotifications((all) => all.filter((_) => _.id !== id))}
          noMargin={options?.noMargin}
        />
      );
    },
    [allNotifications]
  );

  const addNotification = React.useCallback(
    (notification: EnterpriseNotification) =>
      setAllNotifications((_) => [..._, { dismissable: true, ...notification, addedAt: Date.now(), id: uniqueId() }]),
    []
  );
  const clearNotifications = React.useCallback(
    (namespace: string) =>
      setAllNotifications((notifications) => notifications.filter((_) => _.namespace !== namespace)),
    []
  );
  return (
    <NotificationsContext.Provider
      value={{
        renderNotifications,
        addNotification,
        clearNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

/**
 * This hook requires your app to be wrapped with NotificationsProvider
 *
 * it returns an object of the following shape: `{
 *  renderNotifications: (namespace: string) => JSX.Element | null;
 *  addNotification: (notification: Notification) => void;
 * }`
 *
 * call `{renderNotifications('SOME_NAMESPACE')}` to get a JSX.Element
 * and call `addNotification({namespace: 'SOME_NAMESPACE', children: _t('test'), severity: 'info' })`
 * to add a notification to be rendered
 *
 * see the NotificationsContext.test.tsx for more details
 */
export const useNotifications = () => {
  return React.useContext(NotificationsContext);
};
